import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext/AuthContext";
import CustomBottomNavbar from "../../Users/Post Module/FormsBottomNavbar";
import { BASE_URL } from "../../Api/ApiUrls";

const formatPropertyId = (id) => {
  return `BAP${String(id).padStart(4, '0')}`;
};

const getAllImageUrls = (docArray) => {
  if (!docArray || docArray.length === 0) {
    return [];
  }
  
  return docArray.map(doc => {
    if (doc.document) {
      if (doc.document.startsWith('/')) {
        return `${BASE_URL}${doc.document}`;
      }
      return doc.document;
    }
    return null;
  }).filter(url => url !== null);
};

const User_BankAuction_detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const imageUrls = data ? getAllImageUrls(data.bank_pro_doc) : [];
  const hasMultipleImages = imageUrls.length > 1;
  const currentImage = imageUrls[currentImageIndex] || null;

  const nextImage = () => {
    if (imageUrls.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === imageUrls.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (imageUrls.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1
      );
    }
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleCallButtonClick = () => {
    if (data?.bank_contact_details) {
      navigator.clipboard.writeText(data.bank_contact_details)
        .then(() => {
          window.alert(`Contact number copied: ${data.bank_contact_details}\n\nProperty ID: ${formatPropertyId(data.bankprop_id)}`);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          window.alert("Failed to copy contact number");
        });
    } else {
      window.alert("Contact number not available");
    }
  };

  const handleLocationClick = () => {
    setLocationLoading(true);
    
    const openInMaps = (url) => {
      setLocationLoading(false);
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const locationString = `${data.lat},${data.long}`;
    const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationString)}`;
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          const mapsUrl = data.lat && data.long
            ? `https://www.google.com/maps/dir/?api=1&origin=${userLat},${userLng}&destination=${data.lat},${data.long}&travelmode=driving`
            : fallbackUrl;
          openInMaps(mapsUrl);
        },
        (error) => {
          console.error("Error getting location:", error);
          openInMaps(fallbackUrl);
        },
        { timeout: 10000 }
      );
    } else {
      openInMaps(fallbackUrl);
    }
  };
const handleDownloadAuctionFiles = async () => {
  if (!data?.bank_pro_doc || data.bank_pro_doc.length === 0) {
    alert("No auction documents available");
    return;
  }

  try {
    for (let i = 0; i < data.bank_pro_doc.length; i++) {
      const doc = data.bank_pro_doc[i];
      if (!doc.document) continue;

      const fileUrl = doc.document.startsWith("/")
        ? `${BASE_URL}${doc.document}`
        : doc.document;

      console.log("📥 Trying to download:", fileUrl);

      const response = await fetch(fileUrl, {
        method: "GET",
        mode: "cors",
        credentials: "include", // remove if not needed
      });

      console.log("📡 Response received:", response);

      if (!response.ok) {
        console.error("❌ Server responded with error:", {
          status: response.status,
          statusText: response.statusText,
          url: fileUrl,
        });
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log("📦 Blob size:", blob.size, "type:", blob.type);

      const blobUrl = window.URL.createObjectURL(blob);
      const filename = doc.document.split("/").pop() || `auction_file_${i + 1}`;

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);

      await new Promise((res) => setTimeout(res, 500));
    }

    alert(`Downloading ${data.bank_pro_doc.length} file(s)...`);
  } catch (error) {
    console.error("🚨 Download error caught:", error);
    alert("Some files could not be downloaded. Check console for details.");
  }
};





  useEffect(() => {
    const fetchDetail = async () => {
      try {
        if (!userId) {
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${BASE_URL}/auction-property/?user_id=${userId}`
        );

        const selected = res.data.find(
          (item) => String(item.bankprop_id) === String(id)
        );

        setData(selected || null);
        setLoading(false);
        setCurrentImageIndex(0);
      } catch (err) {
        console.error("Auction detail error:", err);
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id, userId]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography>No auction details found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fixed / Sticky Header - exact same as original */}
      <Box
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 1100,
        }}
      >
        <IconButton sx={{ color: "#fff" }} onClick={() => navigate(-1)}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>

        <Typography fontWeight={600} flexGrow={1}>
          Auction Property
        </Typography>

        <Box textAlign="right">
          <Typography variant="caption">
            ID: {formatPropertyId(data.bankprop_id)}
          </Typography>
          <Typography variant="caption" display="block">
            {new Date(data.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pb: 10, // space for bottom navbar
        }}
      >
        <Card sx={{ maxWidth: 420, mx: "auto", boxShadow: 4 }}>
          {/* ================= BODY - original content ================= */}
          <Box sx={{ p: 2 }}>
            {/* IMAGE CAROUSEL - unchanged */}
            <Box position="relative" sx={{ mb: 2 }}>
              {imageUrls.length > 0 ? (
                <>
                  <Box
                    sx={{
                      height: 190,
                      width: "100%",
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 1,
                      backgroundColor: "#e6e0d4",
                    }}
                  >
                    <img
                      src={currentImage}
                      alt={`Property ${currentImageIndex + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const container = e.target.parentElement;
                        if (container.querySelector('.no-image-text')) return;
                        const noImageText = document.createElement('div');
                        noImageText.className = 'no-image-text';
                        noImageText.textContent = 'Image Not Available';
                        noImageText.style.cssText = `
                          position: absolute;
                          top: 50%;
                          left: 50%;
                          transform: translate(-50%, -50%);
                          color: #555;
                          font-weight: 500;
                        `;
                        container.appendChild(noImageText);
                      }}
                    />

                    {hasMultipleImages && (
                      <>
                        <IconButton
                          onClick={prevImage}
                          sx={{
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            width: 28,
                            height: 28,
                            '&:hover': {
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            }
                          }}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        
                        <IconButton
                          onClick={nextImage}
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            width: 28,
                            height: 28,
                            '&:hover': {
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            }
                          }}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </>
                    )}

                    {hasMultipleImages && (
                      <Box sx={{ 
                        position: "absolute",
                        bottom: 8,
                        left: 0,
                        right: 0,
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 0.5,
                      }}>
                        {imageUrls.map((_, index) => (
                          <Box
                            key={index}
                            onClick={() => handleDotClick(index)}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: index === currentImageIndex ? '#1976d2' : '#ccc',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: index === currentImageIndex ? '#1976d2' : '#aaa',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    height: 190,
                    backgroundColor: "#e6e0d4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    No Image Available
                  </Typography>
                </Box>
              )}
            </Box>

            <CardContent sx={{ p: 0 }}>
              {/* PRICE */}
              <Box
                sx={{
                  backgroundColor: "#fff1f0",
                  p: 2,
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body2">Reserve Price</Typography>
                <Typography variant="h6" color="error" fontWeight={700}>
                  ₹{Number(data.reserve_price).toLocaleString()}
                </Typography>
              </Box>

              <Typography variant="body2">
                <b>Property:</b> {data.property_type}
              </Typography>

              <Typography variant="body2" mt={1}>
                <b>City/Town:</b> {data.city_town}
              </Typography>

              <Box mt={1}>
                <Box
                  onClick={handleLocationClick}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }}
                >
                  <Typography variant="body2">
                    <b>Location:</b>
                  </Typography>
                  {locationLoading ? (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  ) : (
                    <LocationOnIcon
                      sx={{ color: "#1976d2", fontSize: "1rem", ml: 0.5 }}
                    />
                  )}
                  {data.location}
                </Box>
              </Box>

              <Typography variant="body2" mt={1}>
                <b>Area:</b> {data.area} {data.units}
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#f9f9f9",
                  p: 2,
                  borderRadius: 2,
                  mt: 2,
                }}
              >
                <InfoRow
                  label="EMD Amount"
                  value={`₹${Number(data.emd_amount).toLocaleString()}`}
                />
                <InfoRow
                  label="Bid Increment"
                  value={`₹${Number(data.bid_increment).toLocaleString()}`}
                />
                <InfoRow
                  label="Auction Start"
                  value={new Date(data.auction_start_datetime).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                />
                <InfoRow
                  label="Auction End"
                  value={new Date(data.auction_end_datetime).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                />
                <InfoRow label="Possession" value={data.possession_status} />
              </Box>

        <Button
  fullWidth
  variant="contained"
  sx={{ mt: 2 }}
  onClick={handleDownloadAuctionFiles}
>
  Download Auction Files
</Button>


              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography fontWeight={600}>{data.bank_name}</Typography>
                  <Typography variant="body2">
                    Contact: {data.bank_contact_details}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CallIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={handleCallButtonClick}
                >
                  Call
                </Button>
              </Box>

              {/* Extra padding so last content isn't hidden under navbar */}
              <Box sx={{ height: 60 }} />
            </CardContent>
          </Box>
        </Card>
      </Box>

      {/* Fixed bottom navbar - unchanged */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
        }}
      >
        <CustomBottomNavbar />
      </Box>
    </Box>
  );
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" mb={1}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

export default User_BankAuction_detail;